interface WebSocketMessage {
  type: string;
  timestamp?: number;
  message?: string;
  data?: any;
  interval_minutes?: number;
}

interface WebSocketTimerConfig {
  interval_minutes: number;
  message: string;
}

class WebSocketClient {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // 1秒
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private messageHandlers: Map<string, (data: any) => void> = new Map();
  private connectionStatus: 'disconnected' | 'connecting' | 'connected' = 'disconnected';
  private userId: string | null = null;

  constructor() {
    this.setupDefaultHandlers();
  }

  private setupDefaultHandlers() {
    // 设置默认消息处理器
    this.on('connection_established', (data) => {
      console.log('WebSocket连接已建立:', data);
      this.connectionStatus = 'connected';
      this.reconnectAttempts = 0;
      this.startHeartbeat();
    });

    this.on('error', (data) => {
      console.error('WebSocket错误:', data);
    });

    this.on('scheduled_push', (data) => {
      console.log('收到定时推送:', data);
      this.showNotification(data.message || '定时提醒');
    });

    this.on('test_push', (data) => {
      console.log('收到测试推送:', data);
      this.showNotification(data.message || '测试消息');
    });

    this.on('timer_started', (data) => {
      console.log('定时器已启动:', data);
    });

    this.on('timer_stopped', (data) => {
      console.log('定时器已停止:', data);
    });

    this.on('timer_updated', (data) => {
      console.log('定时器已更新:', data);
    });

    this.on('status', (data) => {
      console.log('状态信息:', data);
    });

    this.on('frame', (data) => {
      console.log('收到帧数据:', data);
    });

    this.on('frame_sender_started', (data) => {
      console.log('帧发送器已启动:', data);
    });

    this.on('frame_sender_stopped', (data) => {
      console.log('帧发送器已停止:', data);
    });

    this.on('pong', (data) => {
      console.log('💓 收到WebSocket心跳响应:', data);
    });
  }

  public connect(userId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.connectionStatus === 'connected' && this.ws?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      this.userId = userId;
      this.connectionStatus = 'connecting';

      // 使用WSS协议，通过Nginx代理到443端口
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // 使用当前域名，通过Nginx代理
      const host = window.location.hostname;
      const port = window.location.port || (window.location.protocol === 'https:' ? '443' : '80');
      
      // 构建WebSocket URL
      const wsUrl = `${protocol}//${host}:${port}/api/websocket/ws/${userId}`;
      
      console.log('WebSocket连接信息:', {
        protocol,
        host,
        port,
        userId,
        wsUrl
      });

      console.log('正在连接WebSocket:', wsUrl);

      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('WebSocket连接已打开');
          this.connectionStatus = 'connected';
          this.reconnectAttempts = 0;
          this.startHeartbeat();
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('解析WebSocket消息失败:', error);
          }
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket连接已关闭:', event.code, event.reason);
          this.connectionStatus = 'disconnected';
          this.stopHeartbeat();
          this.clearReconnectTimer();

          // 如果不是主动关闭，尝试重连
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket连接错误:', error);
          console.error('WebSocket URL:', wsUrl);
          console.error('WebSocket状态:', this.ws?.readyState);
          console.error('错误详情:', {
            type: error.type,
            target: error.target,
            isTrusted: error.isTrusted
          });
          this.connectionStatus = 'disconnected';
          reject(error);
        };

      } catch (error) {
        console.error('创建WebSocket连接失败:', error);
        this.connectionStatus = 'disconnected';
        reject(error);
      }
    });
  }

  public disconnect(): void {
    this.connectionStatus = 'disconnected';
    this.stopHeartbeat();
    this.clearReconnectTimer();

    if (this.ws) {
      this.ws.close(1000, '主动断开连接');
      this.ws = null;
    }
  }

  public send(message: WebSocketMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket未连接，无法发送消息');
    }
  }

  public on(messageType: string, handler: (data: any) => void): void {
    this.messageHandlers.set(messageType, handler);
  }

  public off(messageType: string): void {
    this.messageHandlers.delete(messageType);
  }

  private handleMessage(message: WebSocketMessage): void {
    const handler = this.messageHandlers.get(message.type);
    if (handler) {
      handler(message);
    } else {
      console.log('未处理的消息类型:', message.type, message);
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    
    // 检查是否为测试模式（URL参数包含test_heartbeat=1）
    const urlParams = new URLSearchParams(window.location.search);
    const isTestMode = urlParams.get('test_heartbeat') === '1';
    const heartbeatInterval = isTestMode ? 3000 : 30000; // 测试模式3秒，正常模式30秒
    
    console.log(`💓 启动WebSocket心跳，间隔: ${heartbeatInterval}ms ${isTestMode ? '(测试模式)' : ''}`);
    
    this.heartbeatInterval = setInterval(() => {
      console.log('💓 发送WebSocket心跳...');
      this.send({ type: 'ping' });
    }, heartbeatInterval);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private clearReconnectTimer(): void {
  // 由 AI 生成：2026-06-22
  // 应用规则：断线时取消待执行的重连，避免重复连接
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimer();
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`计划在 ${delay}ms 后重连 (尝试 ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.connectionStatus === 'disconnected' && this.userId) {
        this.connect(this.userId).catch(error => {
          console.error('重连失败:', error);
        });
      }
    }, delay);
  }

  // 定时推送相关方法
  public startTimer(config: WebSocketTimerConfig): void {
    this.send({
      type: 'start_timer',
      interval_minutes: config.interval_minutes,
      message: config.message
    });
  }

  public stopTimer(): void {
    this.send({ type: 'stop_timer' });
  }

  public updateTimer(config: Partial<WebSocketTimerConfig>): void {
    this.send({
      type: 'update_timer',
      ...config
    });
  }

  public getStatus(): void {
    this.send({ type: 'get_status' });
  }

  public sendTestPush(message?: string): void {
    this.send({
      type: 'test_push',
      message: message || '这是一条测试推送消息'
    });
  }

  // 帧发送器相关方法
  public startFrameSender(): void {
    this.send({ type: 'start_frame_sender' });
  }

  public stopFrameSender(): void {
    this.send({ type: 'stop_frame_sender' });
  }

  // 显示通知
  private showNotification(message: string): void {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Delta定时提醒', {
        body: message,
        icon: '/delta.png',
        badge: '/delta.png',
        tag: 'websocket-notification',
        requireInteraction: false
      });
    } else {
      // 如果浏览器通知不可用，使用alert
      alert(`Delta定时提醒: ${message}`);
    }
  }

  // 获取连接状态
  public getConnectionStatus(): 'disconnected' | 'connecting' | 'connected' {
    return this.connectionStatus;
  }

  // 检查是否已连接
  public isConnected(): boolean {
    return this.connectionStatus === 'connected' && 
           this.ws?.readyState === WebSocket.OPEN;
  }
}

// 创建全局WebSocket客户端实例
export const websocketClient = new WebSocketClient();
export default websocketClient;
